import { useState } from 'react'
import { Drawer, VStack, HStack, Box, Badge, Text, Button, Icon, Portal, CloseButton } from '@chakra-ui/react';
import { FiArrowRight, FiGlobe } from 'react-icons/fi'
import {
  ScriptElementType,
  ELEMENT_TYPE_LABELS,
  ELEMENT_TYPE_COLORS,
  CONDITIONAL_BRANCH_TYPE_LABELS,
} from '../../types/script'
import type {
  ScriptElementFormState,
  TagPickerDto,
  QuestionPickerDto,
  AnswerPickerDto,
  ConditionalBranchType,
} from '../../types/script'

interface ScriptPreviewDrawerProps {
  isOpen: boolean
  onClose: () => void
  elements: ScriptElementFormState[]
  tagOptions: TagPickerDto[]
  questionOptions: QuestionPickerDto[]
  questionAnswersCache?: Record<string, AnswerPickerDto[]>
}

export function ScriptPreviewDrawer({
  isOpen,
  onClose,
  elements,
  tagOptions,
  questionOptions,
  questionAnswersCache = {},
}: ScriptPreviewDrawerProps) {
  const [showSpanish, setShowSpanish] = useState(false)
  const sorted = [...elements].sort((a, b) => a.displayOrder - b.displayOrder)

  const getQuestionLabel = (id: string | null | undefined) =>
    questionOptions.find((q) => q.id === id)

  const getTagLabel = (id: string | null | undefined) =>
    tagOptions.find((t) => t.id === id)?.label ?? 'Unknown tag'

  const getElementRef = (clientId: string | null | undefined) => {
    if (!clientId) return null
    const idx = sorted.findIndex((el) => el.clientId === clientId)
    if (idx === -1) return null
    return `Step ${idx + 1}`
  }

  const getAnswerText = (questionId: string | null | undefined, answerId: string) => {
    if (!questionId) return answerId
    const answers = questionAnswersCache[questionId]
    if (!answers) return `Answer ${answerId.slice(0, 8)}...`
    const answer = answers.find((a) => a.id === answerId)
    return answer?.answerText ?? `Answer ${answerId.slice(0, 8)}...`
  }

  const renderConditionalBranch = (element: ScriptElementFormState) => {
    const branchType = element.conditionalField as ConditionalBranchType | undefined

    if (branchType === 'Tag') {
      return (
        <VStack align="stretch" gap={2}>
          <Text fontSize="sm">
            <Badge colorPalette="purple" fontSize="xs" mr={1}>
              {CONDITIONAL_BRANCH_TYPE_LABELS.Tag}
            </Badge>
            {' '}Check tag:{' '}
            <Text as="span" fontWeight="medium">
              {element.tagId ? getTagLabel(element.tagId) : '?'}
            </Text>
          </Text>
          <HStack fontSize="sm" color="green.600">
            <Icon asChild><FiArrowRight /></Icon>
            <Text>
              Has tag:{' '}
              {getElementRef(element.nextElementOnTrueClientId) ?? 'Continue to next'}
            </Text>
          </HStack>
          <HStack fontSize="sm" color="red.600">
            <Icon asChild><FiArrowRight /></Icon>
            <Text>
              No tag:{' '}
              {getElementRef(element.nextElementOnFalseClientId) ?? 'Continue to next'}
            </Text>
          </HStack>
        </VStack>
      );
    }

    if (branchType === 'Answer') {
      const q = getQuestionLabel(element.conditionalOperator)
      const answerText = element.conditionalValue
        ? getAnswerText(element.conditionalOperator, element.conditionalValue)
        : '?'
      return (
        <VStack align="stretch" gap={2}>
          <Text fontSize="sm">
            <Badge colorPalette="green" fontSize="xs" mr={1}>
              {CONDITIONAL_BRANCH_TYPE_LABELS.Answer}
            </Badge>
            {' '}If answer to{' '}
            <Text as="span" fontWeight="medium">
              {q?.label ?? '?'}
            </Text>
            {' '}is{' '}
            <Text as="span" fontWeight="medium">
              "{answerText}"
            </Text>
          </Text>
          <HStack fontSize="sm" color="green.600">
            <Icon asChild><FiArrowRight /></Icon>
            <Text>
              Match:{' '}
              {getElementRef(element.nextElementOnTrueClientId) ?? 'Continue to next'}
            </Text>
          </HStack>
          <HStack fontSize="sm" color="red.600">
            <Icon asChild><FiArrowRight /></Icon>
            <Text>
              No match:{' '}
              {getElementRef(element.nextElementOnFalseClientId) ?? 'Continue to next'}
            </Text>
          </HStack>
        </VStack>
      );
    }

    if (branchType === 'Prompt') {
      return (
        <VStack align="stretch" gap={2}>
          <Text fontSize="sm">
            <Badge colorPalette="orange" fontSize="xs" mr={1}>
              {CONDITIONAL_BRANCH_TYPE_LABELS.Prompt}
            </Badge>
          </Text>
          <Text fontSize="sm" fontStyle="italic" color="gray.600">
            "{element.promptText || 'No prompt text'}"
          </Text>
          {element.promptResponses && element.promptResponses.length > 0 ? (
            element.promptResponses.map((pr, i) => (
              <HStack key={i} fontSize="sm" color="info.fg">
                <Icon asChild><FiArrowRight /></Icon>
                <Text>
                  "{pr.label || '?'}" :{' '}
                  {getElementRef(pr.targetClientId) ?? 'Continue to next'}
                </Text>
              </HStack>
            ))
          ) : (
            <Text fontSize="xs" color="gray.400" fontStyle="italic">
              No responses configured
            </Text>
          )}
        </VStack>
      );
    }

    // Fallback for legacy/untyped conditionals
    return (
      <VStack align="stretch" gap={2}>
        <Text fontSize="sm">
          If{' '}
          <Text as="span" fontWeight="medium">
            {element.conditionalField || '?'}
          </Text>{' '}
          {element.conditionalOperator || '?'}{' '}
          <Text as="span" fontWeight="medium">
            {element.conditionalValue || '?'}
          </Text>
        </Text>
        <HStack fontSize="sm" color="green.600">
          <Icon asChild><FiArrowRight /></Icon>
          <Text>
            True:{' '}
            {getElementRef(element.nextElementOnTrueClientId) ?? 'Continue to next'}
          </Text>
        </HStack>
        <HStack fontSize="sm" color="red.600">
          <Icon asChild><FiArrowRight /></Icon>
          <Text>
            False:{' '}
            {getElementRef(element.nextElementOnFalseClientId) ?? 'Continue to next'}
          </Text>
        </HStack>
      </VStack>
    );
  }

  const renderSurveyQuestion = (element: ScriptElementFormState) => {
    const q = getQuestionLabel(element.surveyQuestionId)
    const answerBranches = element.answerBranches ?? {}
    const branchEntries = Object.entries(answerBranches)

    return q ? (
      <VStack align="stretch" gap={1}>
        <Text fontSize="sm" fontWeight="medium">
          {q.label}
        </Text>
        <Text fontSize="sm" color="gray.600">
          {q.question}
        </Text>
        {branchEntries.length > 0 && (
          <Box mt={1} pl={3} borderLeftWidth="2px" borderLeftColor="green.300">
            <Text fontSize="xs" fontWeight="semibold" color="green.600" mb={1}>
              Answer Branches:
            </Text>
            {branchEntries.map(([answerId, targetClientId]) => (
              <HStack key={answerId} fontSize="xs" color="gray.600" gap={1}>
                <Icon color="green.400" boxSize={3} asChild><FiArrowRight /></Icon>
                <Text>
                  "{getAnswerText(element.surveyQuestionId, answerId)}" :{' '}
                  {getElementRef(targetClientId) ?? 'Continue to next'}
                </Text>
              </HStack>
            ))}
          </Box>
        )}
      </VStack>
    ) : (
      <Text fontSize="sm" color="gray.400" fontStyle="italic">
        No question selected
      </Text>
    );
  }

  return (
    <Drawer.Root open={isOpen} placement='end' size='md' onOpenChange={e => {
      if (!e.open) {
        onClose();
      }
    }}>
      <Portal>

        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content>
            <Drawer.CloseTrigger asChild><CloseButton size="sm" /></Drawer.CloseTrigger>
            <Drawer.Header>
              <HStack justify="space-between" pr={8}>
                <Text>Script Preview</Text>
                <Button
                  size="xs"
                  variant={showSpanish ? 'solid' : 'outline'}
                  colorPalette="brand"
                  onClick={() => setShowSpanish(!showSpanish)}><Icon asChild><FiGlobe /></Icon>{showSpanish ? 'ES' : 'EN'}</Button>
              </HStack>
            </Drawer.Header>
            <Drawer.Body>
              {sorted.length === 0 ? (
                <VStack py={12}>
                  <Text color="gray.500">
                    Add elements to preview the script flow.
                  </Text>
                </VStack>
              ) : (
                <VStack gap={4} align="stretch">
                  {sorted.map((element, index) => (
                    <Box
                      key={element.clientId}
                      p={4}
                      borderWidth="1px"
                      borderRadius="md"
                      borderLeftWidth="4px"
                      borderLeftColor={`${ELEMENT_TYPE_COLORS[element.elementType]}.400`}
                    >
                      <HStack mb={2}>
                        <Text fontSize="xs" fontWeight="bold" color="gray.400">
                          Step {index + 1}
                        </Text>
                        <Badge
                          colorPalette={ELEMENT_TYPE_COLORS[element.elementType]}
                          fontSize="xs"
                        >
                          {ELEMENT_TYPE_LABELS[element.elementType]}
                        </Badge>
                      </HStack>

                      {element.elementType === ScriptElementType.Text && (
                        <Text fontSize="sm" whiteSpace="pre-wrap">
                          {showSpanish && element.textContentSpanish
                            ? element.textContentSpanish
                            : element.textContent || (
                                <Text as="span" color="gray.400" fontStyle="italic">
                                  No text entered
                                </Text>
                              )}
                        </Text>
                      )}

                      {element.elementType === ScriptElementType.SurveyQuestion &&
                        renderSurveyQuestion(element)}

                      {element.elementType === ScriptElementType.TagAssignment && (
                        <Text fontSize="sm">
                          Assign tag:{' '}
                          <Text as="span" fontWeight="medium">
                            {element.tagId
                              ? getTagLabel(element.tagId)
                              : 'No tag selected'}
                          </Text>
                        </Text>
                      )}

                      {element.elementType === ScriptElementType.ConditionalBranch &&
                        renderConditionalBranch(element)}
                    </Box>
                  ))}
                </VStack>
              )}
            </Drawer.Body>
          </Drawer.Content>
        </Drawer.Positioner>

      </Portal>
    </Drawer.Root>
  );
}
